import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { AddToCartButton } from '../../../Checkout'
import { useProductSliderVariants } from '../../../Theme'
import { CompactProductCard, SectionHeader } from '../../../UiBlocks'
import { PRODUCT_SLIDER_BREAKPOINTS } from '../../constants'
import { useProductSliderData } from '../../hooks'
import { LABELS } from '../../lib'
import { NavigationButton } from '../shared'
import { ProductSliderSkeleton } from './ProductSliderSkeleton'

import type { Product } from '../../../UiBlocks'
import type { HomepageProductSlider } from '../../types'

interface ProductSliderProps {
  slider: HomepageProductSlider
  /** Global loading state (config loading) - shows skeleton before query starts */
  isLoading?: boolean
}

/**
 * Cart button for CompactProductCard, shared by the slider and grid layouts:
 * labelled full-width row below md, icon button from md up.
 */
const renderAddToCart = (product: Product) => (
  <AddToCartButton sku={product.sku} iconFromMd />
)

/**
 * ProductSlider - Carousel or static grid of products
 *
 * Layout switches by `slider.display_type`:
 * - 'slider' → Swiper carousel (2 / 3 / 5 responsive)
 * - 'grid'   → Static CSS grid (2 / 3 / 5 responsive)
 *
 * Uses CompactProductCard (compact: image + name + price + add-to-cart).
 * The tall ProductCard is reserved for related/cross-sell contexts.
 */
export function ProductSlider({ slider, isLoading: configLoading = false }: ProductSliderProps) {
  const {
    productSliderSectionVariants,
    productSliderWrapperVariants,
    productSliderSwiperVariants,
    productSliderGridVariants,
    productSliderGridItemVariants,
  } = useProductSliderVariants()

  const { products, isLoading: productsLoading } = useProductSliderData({ slider })

  if (configLoading || productsLoading) {
    return <ProductSliderSkeleton />
  }

  if (products.length === 0) {
    return null
  }

  const isGrid = slider.display_type === 'grid'

  return (
    <section className={productSliderSectionVariants()}>
      <SectionHeader size="lg" centered>{slider.title}</SectionHeader>

      <div className={productSliderWrapperVariants()}>
        {isGrid ? (
          <div className={productSliderGridVariants()}>
            {products.map((product) => (
              <div key={product.sku} className={productSliderGridItemVariants()}>
                <CompactProductCard
                  product={product}
                  className="h-full"
                  priceSuffix={LABELS.PRICE_SUFFIX}
                  renderActions={renderAddToCart}
                />
              </div>
            ))}
          </div>
        ) : (
          <SliderCarousel slider={slider} products={products} swiperClass={productSliderSwiperVariants()} />
        )}
      </div>
    </section>
  )
}

interface SliderCarouselProps {
  slider: HomepageProductSlider
  products: Product[]
  swiperClass: string
}

function SliderCarousel({ slider, products, swiperClass }: SliderCarouselProps) {
  const { productSliderSlideVariants } = useProductSliderVariants()

  const navSelectors = {
    prevEl: `.product-swiper-prev-${slider.id}`,
    nextEl: `.product-swiper-next-${slider.id}`,
    prevClass: `product-swiper-prev-${slider.id}`,
    nextClass: `product-swiper-next-${slider.id}`,
  }

  const enableLoop = products.length > 5

  return (
    <Swiper
      modules={[Navigation]}
      spaceBetween={0}
      slidesPerView={2}
      loop={enableLoop}
      navigation={{
        prevEl: navSelectors.prevEl,
        nextEl: navSelectors.nextEl,
      }}
      breakpoints={PRODUCT_SLIDER_BREAKPOINTS}
      className={swiperClass}
    >
      {products.map((product) => (
        <SwiperSlide key={product.sku} className={productSliderSlideVariants()}>
          <CompactProductCard
            product={product}
            className="h-full"
            priceSuffix={LABELS.PRICE_SUFFIX}
            renderActions={renderAddToCart}
          />
        </SwiperSlide>
      ))}

      <NavigationButton direction="prev" size="product" className={navSelectors.prevClass} />
      <NavigationButton direction="next" size="product" className={navSelectors.nextClass} />
    </Swiper>
  )
}
